import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../../../../api/client';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { useToastStore } from '../../../../stores/toastStore';
import { ProviderSelect } from '../../../schema/components/ProviderSelect';
import { SampleUploader } from '../components/SampleUploader';
import { useAddSample, useDraft, useStartDiscovery } from '../hooks';

/**
 * Step 1 — upload between 1 and 10 PDF samples and kick off the analysis.
 *
 * When the route includes `:draftId` we render in "resume" mode: existing
 * samples are listed (read-only, file-name basis is not available so we show
 * a count badge), and the dropzone is reused to append more samples via
 * `AddDiscoverySampleUseCase`. The button label and target route adapt to
 * whether the user is starting fresh or coming back to an existing draft.
 *
 * On success (fresh draft) we navigate to the analyzing step using the
 * freshly minted draftId returned by the server. In resume mode we also
 * navigate to analyzing after the last add-sample mutation completes so the
 * orchestrator picks up the new inputs.
 */
export function DiscoveryUploadStep(): JSX.Element {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const pushToast = useToastStore((s) => s.push);
  const startDiscovery = useStartDiscovery();
  const addSample = useAddSample(draftId ?? '');
  const { data: draft, isLoading: draftLoading } = useDraft(draftId);

  const [hintName, setHintName] = useState<string>('');
  const [hintCategory, setHintCategory] = useState<string>('');
  // Empty string means "no explicit choice" — the backend then applies the
  // default-selection logic (alphabetical-first configured provider for the tenant).
  const [providerId, setProviderId] = useState<string>('');

  const resumeMode = draftId !== undefined;

  const handleFreshStart = (paths: string[]): void => {
    startDiscovery.mutate(
      {
        sampleBlobPaths: paths,
        hintName: hintName.trim() !== '' ? hintName.trim() : null,
        hintCategory: hintCategory.trim() !== '' ? hintCategory.trim() : null,
        modelProviderId: providerId !== '' ? providerId : null,
      },
      {
        onSuccess: (created) => {
          pushToast({
            tone: 'success',
            title: 'Análisis iniciado',
            description: `Estamos procesando ${paths.length} muestra(s).`,
          });
          navigate(`/document-types/discover/${encodeURIComponent(created.id)}/analyzing`);
        },
        onError: (err) => {
          const message =
            err instanceof ApiError ? err.message : 'No se pudo iniciar el análisis.';
          pushToast({ tone: 'error', title: 'Error al iniciar', description: message });
        },
      },
    );
  };

  const handleResume = async (paths: string[]): Promise<void> => {
    if (draftId === undefined) {
      return;
    }
    try {
      // Single batched call: per-file requests would start the analysis with a partial sample
      // set and collide on the per-draft orchestration instance.
      await addSample.mutateAsync({ sampleBlobPaths: paths });
      pushToast({
        tone: 'success',
        title: 'Muestras añadidas',
        description: `Se han añadido ${paths.length} muestra(s) al descubrimiento.`,
      });
      navigate(`/document-types/discover/${encodeURIComponent(draftId)}/analyzing`);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'No se pudieron añadir las muestras.';
      pushToast({ tone: 'error', title: 'Error al añadir muestras', description: message });
    }
  };

  const handleSamples = (paths: string[]): void => {
    if (paths.length === 0) {
      return;
    }
    if (resumeMode) {
      void handleResume(paths);
      return;
    }
    handleFreshStart(paths);
  };

  const busy = startDiscovery.isPending || addSample.isPending;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">
          {resumeMode && draft?.sourceDocumentTypeSlug != null
            ? 'Añadir muestras para reeducar'
            : resumeMode
              ? 'Añadir muestras al descubrimiento'
              : 'Crear desde documentos'}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {resumeMode && draft?.sourceDocumentTypeSlug != null ? (
            <>
              Sube PDFs representativos del tipo{' '}
              <code className="font-mono text-xs">{draft.sourceDocumentTypeSlug}</code>{' '}
              para que el motor proponga mejoras en su esquema.
            </>
          ) : resumeMode ? (
            'Puedes añadir más muestras y relanzar el análisis con la información ampliada.'
          ) : (
            'Con 1 documento basta: el motor analiza el OCR y te propone el esquema completo. Puedes subir más (hasta 10) para reforzar la propuesta.'
          )}
        </p>
      </header>

      {resumeMode ? (
        <section className="card p-5" data-testid="discovery-existing-samples">
          <h2 className="text-base font-semibold text-slate-900">Muestras ya subidas</h2>
          {draftLoading || draft === undefined ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
              <LoadingSpinner />
              <span>Cargando descubrimiento…</span>
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-500">
              Este descubrimiento tiene{' '}
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                {draft.sampleCount} subida(s)
              </span>{' '}
              · {draft.processedSampleCount} procesada(s).
            </p>
          )}
        </section>
      ) : (
        <section className="card p-5">
          <h2 className="text-base font-semibold text-slate-900">
            Pistas para el motor (opcional)
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Estos hints aceleran la propuesta cuando ya tienes una idea del tipo.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <label className="label" htmlFor="hint-name">
                Nombre tentativo
              </label>
              <input
                id="hint-name"
                className="input"
                value={hintName}
                onChange={(event) => setHintName(event.target.value)}
                placeholder="Factura proveedor"
                disabled={startDiscovery.isPending}
              />
            </div>
            <div>
              <label className="label" htmlFor="hint-category">
                Categoría
              </label>
              <input
                id="hint-category"
                className="input"
                value={hintCategory}
                onChange={(event) => setHintCategory(event.target.value)}
                placeholder="Compras"
                disabled={startDiscovery.isPending}
              />
            </div>
            <div className="md:col-span-2">
              <label className="label" htmlFor="discovery-provider">
                Modelo IA para el descubrimiento
              </label>
              <ProviderSelect
                value={providerId}
                onChange={setProviderId}
                disabled={startDiscovery.isPending}
              />
              <p className="mt-1 text-xs text-slate-500">
                Opcional. Si no eliges, se usará el primer proveedor configurado
                para tu tenant. Podrás cambiar el modelo final al publicar el tipo.
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="card p-5">
        <h2 className="text-base font-semibold text-slate-900">
          {resumeMode ? 'Añadir más muestras' : 'Muestras'}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Solo se aceptan PDFs. Máximo 10 archivos, 20 MB por archivo.
        </p>
        <div className="mt-4">
          <SampleUploader onSamplesSelected={handleSamples} busy={busy} />
        </div>
      </section>
    </div>
  );
}
